import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import * as s from "../InputPage.styles";
import { AnalysisForm, AreaUnit, areaUnitList, cropList } from "../machine";
import { useFormattedNumberField } from "./useFormattedNumberField";
import {
  convertPriceUnit,
  convertQuantityUnit,
  formatManToEokMan,
  toM2,
} from "../function";

interface Props {
  isCommunal: boolean;
  formData: AnalysisForm;
  setFormData: Dispatch<SetStateAction<AnalysisForm>>;
  quantityRef: React.RefObject<HTMLDivElement>;
  salesRef: React.RefObject<HTMLDivElement>;
}

function CropSection({
  isCommunal,
  formData,
  setFormData,
  quantityRef,
  salesRef,
}: Props) {
  const [isCustomCrop, setIsCustomCrop] = useState(false);

  const [unitPriceInputMode, setUnitPriceInputMode] = useState<"sale" | "unit">(
    "unit"
  );
  const toggleUnitPriceInputMode = () => {
    setUnitPriceInputMode((prev) => (prev === "sale" ? "unit" : "sale"));
  };

  const [openUnitChange, setOpenUnitChange] = useState(false);
  const areaUnitRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      // 열려있는 드롭다운에 따라 참조할 ref 선택
      const currentRef = areaUnitRef.current;

      if (currentRef && !currentRef.contains(target)) {
        setOpenUnitChange(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const changeAreaUnit = (unit: AreaUnit) => {
    setFormData((prev) => {
      const baseQuantity = convertQuantityUnit(prev.quantity, prev.unit, unit);
      const baseUnitPrice = convertPriceUnit(
        prev.farmAgentUnitPrice,
        prev.unit,
        unit
      );
      return {
        ...prev,
        quantity: baseQuantity,
        farmAgentUnitPrice: baseUnitPrice,
        unit: unit,
      };
    });
    setOpenUnitChange(false);
  };

  // 연 생산량
  const {
    display: quantityDisplay,
    handleChange: handleQuantityChange,
    handleBlur: handleQuantityBlur,
  } = useFormattedNumberField({
    baseValue: formData.quantity,
    isPrice: false,
    onChangeBaseValue: (value) => {
      const areaM2 = toM2(formData.allArea, "평");
      const quantityPerM2 = value / toM2(1, formData.unit);
      const totalQuantity = quantityPerM2 * areaM2;

      setFormData((prev) => {
        if (!value) {
          return {
            ...prev,
            quantity: value,
            unitPrice: 0,
          };
        }
        // 연매출 입력받고 있으면 단가 자동 계산
        if (unitPriceInputMode === "sale") {
          return {
            ...prev,
            quantity: value,
            unitPrice: Math.round((prev.sales * 10000) / totalQuantity),
          };
        } else {
          return {
            ...prev,
            quantity: value,
            sales: Math.round((prev.unitPrice * totalQuantity) / 10000),
          };
        }
      });
    },
  });

  // 연 매출
  const {
    display: salesDisplay,
    handleChange: handleSalesChange,
    handleBlur: handleSalesBlur,
  } = useFormattedNumberField({
    baseValue: formData.sales,
    onChangeBaseValue: (value) => {
      setFormData((prev) => {
        const areaM2 = toM2(formData.allArea, "평");
        const quantityPerM2 = prev.quantity / toM2(1, formData.unit);
        const totalQuantity = quantityPerM2 * areaM2;
        return {
          ...prev,
          sales: value,
          unitPrice: Math.round((value * 10000) / totalQuantity),
        };
      });
    },
  });

  // 단가
  const {
    display: unitPriceDisplay,
    handleChange: handleUnitPriceChange,
    handleBlur: handleUnitPriceBlur,
  } = useFormattedNumberField({
    baseValue: formData.unitPrice,
    onChangeBaseValue: (value) => {
      setFormData((prev) => {
        const areaM2 = toM2(formData.allArea, "평");
        const quantityPerM2 = prev.quantity / toM2(1, formData.unit);
        const totalQuantity = quantityPerM2 * areaM2;

        return {
          ...prev,
          unitPrice: value,
          sales: Math.round((totalQuantity * value) / 10000),
        };
      });
    },
  });

  return (
    <s.Section>
      <s.SectionTitleContainer>
        <s.SectionTitle>2. 작물</s.SectionTitle>
        {isCommunal && (
          <s.UnitWrapper>
            <s.UnitChangeButton style={{ background: "transparent" }}>
              <span>기준</span>
              자경 농지
            </s.UnitChangeButton>
          </s.UnitWrapper>
        )}
        <s.UnitWrapper ref={areaUnitRef}>
          <s.UnitChangeButton onClick={() => setOpenUnitChange(true)}>
            <span>단위</span>
            {formData.unit}
          </s.UnitChangeButton>
          {openUnitChange && (
            <s.UnitChangeDropdown>
              {areaUnitList.map((i) => (
                <s.UnitChangeDropdownItem
                  key={i}
                  onClick={() => changeAreaUnit(i)}
                  $active={formData.unit === i}
                >
                  {i}
                  {formData.unit === i && (
                    <img src="/asset/images/check/Check24Green2.svg" alt="" />
                  )}
                </s.UnitChangeDropdownItem>
              ))}
            </s.UnitChangeDropdown>
          )}
        </s.UnitWrapper>
      </s.SectionTitleContainer>
      <s.SectionInner $top={8}>
        <s.OptionWrapper>
          {cropList.map((c) => (
            <s.Option
              key={c}
              $active={!isCustomCrop && c === formData.crop}
              onClick={() => {
                setFormData((prev) => ({ ...prev, crop: c }));
                setIsCustomCrop(false);
              }}
            >
              {c}
            </s.Option>
          ))}
          <s.Option
            $active={isCustomCrop}
            onClick={() => {
              setFormData((prev) => ({ ...prev, crop: "" }));
              setIsCustomCrop(true);
            }}
          >
            직접입력
          </s.Option>
        </s.OptionWrapper>
      </s.SectionInner>

      {isCustomCrop && (
        <s.SectionInner $top={8}>
          <s.Label>
            <s.LabelTitle>작물</s.LabelTitle>
          </s.Label>
          <s.InputWrapper>
            <s.Input
              type="text"
              placeholder="어떤 작물을 재배하고 있나요?"
              value={formData.crop}
              onChange={(e) => {
                setFormData((prev) => ({
                  ...prev,
                  crop: e.target.value,
                }));
              }}
            />
          </s.InputWrapper>
        </s.SectionInner>
      )}

      <s.SectionInner $top={8} ref={quantityRef}>
        <s.Label>
          <s.LabelTitle>경작기간 생산량</s.LabelTitle>
        </s.Label>
        <s.InputMessageContainer>
          <s.InputWrapper $error={!formData.quantity}>
            <s.Input
              type="text"
              inputMode="decimal"
              placeholder="1년에 수확하는 양을 입력해주세요"
              value={quantityDisplay}
              onChange={(e) => handleQuantityChange(e.target.value)}
              onBlur={handleQuantityBlur}
            />
            <span>kg/{formData.unit}</span>
          </s.InputWrapper>
          {!formData.quantity && (
            <s.InputErrorMessage>
              생산량은 필수로 입력해야 합니다.
            </s.InputErrorMessage>
          )}
        </s.InputMessageContainer>
      </s.SectionInner>

      {unitPriceInputMode === "sale" ? (
        <s.SectionInner $top={8} ref={salesRef}>
          <s.Label>
            <s.LabelTitle>
              경작기간 매출
              <s.ChangeInputUnit onClick={toggleUnitPriceInputMode}>
                <img src="/asset/images/change/refresh-black.svg" alt="" />
                단가 입력
              </s.ChangeInputUnit>
            </s.LabelTitle>
          </s.Label>
          <s.InputMessageContainer>
            <s.InputWrapper $error={!salesDisplay || salesDisplay === "0"}>
              <s.Input
                type="text"
                inputMode="decimal"
                placeholder="총 연 매출을 입력해주세요"
                value={salesDisplay}
                onChange={(e) => handleSalesChange(e.target.value)}
                onBlur={handleSalesBlur}
              />
              <span>만원</span>
            </s.InputWrapper>
            {(!salesDisplay || salesDisplay === "0") && (
              <s.InputErrorMessage>
                매출은 필수로 입력해야 합니다.
              </s.InputErrorMessage>
            )}
            {formData.sales > 0 && (
              <s.InputMessage>
                {formatManToEokMan(formData.sales)}
              </s.InputMessage>
            )}
          </s.InputMessageContainer>
        </s.SectionInner>
      ) : (
        <s.SectionInner $top={8} ref={salesRef}>
          <s.Label>
            <s.LabelTitle>
              단가
              <s.ChangeInputUnit onClick={toggleUnitPriceInputMode}>
                <img src="/asset/images/change/refresh-black.svg" alt="" />
                매출 입력
              </s.ChangeInputUnit>
            </s.LabelTitle>
          </s.Label>
          <s.InputMessageContainer>
            <s.InputWrapper $error={!formData.unitPrice}>
              <s.Input
                type="text"
                inputMode="decimal"
                placeholder="작물의 kg당 단가를 입력해주세요"
                value={unitPriceDisplay}
                onChange={(e) => handleUnitPriceChange(e.target.value)}
                onBlur={handleUnitPriceBlur}
              />
              <span>원 / kg</span>
            </s.InputWrapper>
            {!formData.unitPrice && (
              <s.InputErrorMessage>
                단가는 필수로 입력해야 합니다.
              </s.InputErrorMessage>
            )}
          </s.InputMessageContainer>
        </s.SectionInner>
      )}

      {unitPriceInputMode === "sale" ? (
        <s.SectionInner>
          <s.Label>
            <s.LabelTitle>kg 당 단가</s.LabelTitle>
            매출을 입력하면 kg당 단가가 계산됩니다.
          </s.Label>
          <s.FixedValue>
            {formData.unitPrice.toLocaleString()}원 / kg
          </s.FixedValue>
        </s.SectionInner>
      ) : (
        <s.SectionInner>
          <s.Label>
            <s.LabelTitle>경작기간 매출</s.LabelTitle>
            단가를 입력하면 경작기간 매출이 계산됩니다.
          </s.Label>
          <s.FixedValue>{formatManToEokMan(formData.sales)}</s.FixedValue>
        </s.SectionInner>
      )}
    </s.Section>
  );
}

export default CropSection;
